<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { trackPageView } from "@/analytics/app-analytics";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import {
  useChangelogInfiniteQuery,
  useMarkChangelogSeenMutation,
} from "@/stores/queries/changelog.query";

const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
  useChangelogInfiniteQuery();
const { mutate: markSeen } = useMarkChangelogSeenMutation();

const entries = computed(() =>
  (data.value?.pages.flatMap((p) => p.entries) ?? []).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
);
const unseenCount = computed(() => data.value?.pages[0]?.unseenCount ?? 0);

const md = new MarkdownIt({ linkify: true, breaks: true });

const defaultLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens: any[], idx: number, options: any, _env: any, self: any) =>
    self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet("target", "_blank");
  tokens[idx].attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen(tokens, idx, options, env, self);
};

function renderMarkdown(body: string): string {
  return DOMPurify.sanitize(md.render(body), { ADD_ATTR: ["target", "rel"] });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// markSeen à la fermeture du panel Bootstrap
let panelEl: HTMLElement | null = null;

function onPanelHide() {
  if (unseenCount.value > 0) markSeen();
}

function onPanelShow() {
  trackPageView("panel-news");
}

function bindHideEvent(el: HTMLElement | null) {
  panelEl = el;
  if (el) {
    el.addEventListener("hide.bs.offcanvas", onPanelHide);
    el.addEventListener("show.bs.offcanvas", onPanelShow);
  }
}

onBeforeUnmount(() => {
  panelEl?.removeEventListener("hide.bs.offcanvas", onPanelHide);
  panelEl?.removeEventListener("show.bs.offcanvas", onPanelShow);
});
</script>

<template>
  <a data-bs-toggle="offcanvas" href="#offcanvasWhatsNew" role="button" aria-controls="offcanvasWhatsNew"
    :data-tooltip="$t('navbar.whatsNewTooltip')" :aria-label="$t('navbar.whatsNewTooltip')"
    class="whats-new-trigger dr-tooltip">
    <el-icon size="18" class="text-dark">
      <i class="bi bi-bell"></i>
    </el-icon>
    <span v-if="unseenCount > 0" class="unseen-badge">
      {{ unseenCount > 9 ? "9+" : unseenCount }}
    </span>
  </a>

  <div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasWhatsNew" aria-labelledby="offcanvasWhatsNewLabel"
    :ref="(el) => bindHideEvent(el as HTMLElement)">
    <div class="offcanvas-header bg-white border-bottom">
      <h4 class="offcanvas-title d-flex align-items-center gap-2" id="offcanvasWhatsNewLabel">
        <i class="bi bi-bell-fill text-warning"></i>
        Nouveautés
        <span v-if="unseenCount > 0" class="header-badge">
          {{ unseenCount }} nouveau{{ unseenCount > 1 ? "x" : "" }}
        </span>
      </h4>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
    </div>

    <div class="offcanvas-body bg-white">
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100">
        <div class="spinner-border spinner-border-sm text-secondary" role="status" />
      </div>

      <div v-else-if="entries.length === 0" class="text-center text-muted py-5">
        <i class="bi bi-inbox fs-2 d-block mb-2"></i>
        Aucune nouveauté pour le moment
      </div>

      <template v-else>
        <div class="changelog-list">
          <div v-for="entry in entries" :key="entry.id" class="changelog-entry">
            <div class="changelog-header">
              <span class="changelog-version">v{{ entry.version }}</span>
              <span class="changelog-date">{{ formatDate(entry.createdAt) }}</span>
              <span v-if="entry.isNew" class="new-badge">Nouveau</span>
            </div>

            <div v-for="(item, i) in entry.entries" :key="i" class="changelog-item">
              <div class="changelog-item-title">{{ item.title }}</div>
              <div class="changelog-item-body markdown-body" v-html="renderMarkdown(item.body)" />
            </div>
          </div>
        </div>

        <div class="load-more-area">
          <div v-if="isFetchingNextPage" class="d-flex justify-content-center py-3">
            <div class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
          <button v-else-if="hasNextPage" class="load-more-btn" @click="fetchNextPage()">
            Charger plus
          </button>
          <p v-else-if="entries.length > 10" class="text-center text-muted small py-2 mb-0">
            Vous avez tout vu !
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.whats-new-trigger {
  position: relative;
}

.unseen-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

.header-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background-color: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 99px;
}

.changelog-list {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.changelog-entry {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.changelog-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--bs-border-color);
}

.changelog-version {
  font-size: 1rem;
  font-weight: 700;
  color: var(--bs-body-color);
}

.changelog-date {
  font-size: 0.78rem;
  color: var(--bs-secondary-color);
  flex: 1;
}

.new-badge {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background-color: #ef4444;
  color: white;
  padding: 2px 7px;
  border-radius: 99px;
  flex-shrink: 0;
}

.changelog-item {
  padding: 0.65rem 0.75rem;
  background-color: var(--bs-secondary-bg);
  border-radius: 8px;
}

.changelog-item-title {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.3rem;
  color: var(--bs-body-color);
}

.load-more-area {
  margin-top: 1rem;
  text-align: center;
}

.load-more-btn {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.82rem;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }
}
</style>

<style lang="scss">
.markdown-body {
  font-size: 0.82rem;
  color: var(--bs-secondary-color);
  line-height: 1.5;

  p {
    margin: 0 0 0.4rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    padding-left: 1.2rem;
    margin: 0.2rem 0 0;
  }

  li {
    margin-bottom: 0.15rem;
  }

  strong {
    color: var(--bs-body-color);
  }

  a {
    color: var(--el-color-primary);
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }

  code {
    background: var(--bs-tertiary-bg);
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.85em;
  }
}
</style>
